/**
 * SIGNED IN, WITH A HOME LINKED, BEFORE ANYTHING OF IT HAS COME DOWN. Home shows this while every
 * section of /api/home is still arriving: before ops grants the membership, and until the first
 * push of the first fill lands. Once anything has come, Home is drawn instead, and says the rest is
 * on its way.
 *
 * It says what this page will become and gives the phone and the hours, as the page that says to
 * call does, and nothing more: no household name, no visit and no invoice, because none has come
 * down yet and home never shows what ops has not sent. A sign-out that does not go through leaves
 * her here, with the focus on Sign out and a line under it that says so (`components/SignOut.tsx`).
 *
 * IT SAYS IT IS WORKING (provisional P106, ops #81). The owner's first sign-in to production, on
 * 2026-10-19, met this page with nothing to say anything was happening, and asked for a spinner, or
 * a word on how long it takes. So, straight under the heading:
 *
 * - A RING THAT TURNS beside the words, slowly, and only where the device allows motion
 *   (`motion-safe:`); where she has asked it for less motion, it stands still. It is drawn for the
 *   eye alone (`aria-hidden`): the words say the state.
 * - A STATUS a screen reader is told of when it changes (`role="status"`, polite): what is
 *   happening and how long it usually takes, in ops's own account of a first fill, the household
 *   among its first rows (its contract, section 3, "The first fill"), and that the page updates by
 *   itself.
 * - THE PAGE READS ITSELF AGAIN, gently (`lib/readAgain.ts`): fifteen seconds, backing off to a
 *   minute, nothing while the tab is hidden, and Home is drawn in its place once the household
 *   comes, with the focus on Home's heading (`Home.tsx`). After an hour in view with nothing come it
 *   stops, the ring goes, the status says so, and "Check again" reads once more and starts over,
 *   handing the focus to the status, since the button goes with it. The way to call stays under it
 *   all.
 *
 * Its header is every signed-in place's (round five, slice A4): the name a link to Home, and Account
 * left of Call, which leads to Account as it stands before the grant, the address she signed in with
 * and where she is signed in, so the button is on every signed-in screen with a home linked.
 *
 * AN INVITATION THAT WAITS FOR HER CODE is said above its content, as Home says it (P118, found on
 * staging, 2026-11-05): while /api/me says one waits for the address or number she signed in with,
 * the notice and its fresh code (`components/InvitationWaits.tsx`), so the page whose household is
 * still coming down never hides another household only a code can open.
 */
import { useRef, type ComponentProps } from 'react';
import { GHOST_BUTTON, NAVY_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { COLUMN } from '../components/column.ts';
import { Header } from '../components/Header.tsx';
import { InvitationWaits } from '../components/InvitationWaits.tsx';
import { SignOutFailed, useSignOut } from '../components/SignOut.tsx';
import { HOURS, PHONE_HREF, PHONE_SHOWN } from '../contact.ts';
import { useReadAgain } from '../lib/readAgain.ts';
import { HOLDING, UPDATES_BY_ITSELF } from '../words.ts';

/** The longest wait between two reads here: a minute, so she sees her home soon after it comes. */
export const HOLDING_CAP_MS = 60 * 1000;

/** How long it reads itself again in view before it stops and says so: an hour. */
export const HOLDING_BOUND_MS = 60 * 60 * 1000;

interface HoldingProps {
  readonly onSignedOut: () => void;
  /** Reads Home again, from which Home is drawn in place of this once the household has come. */
  readonly reload: () => void;
  /**
   * What the notice that an invitation waits for her code is drawn from, above the page's content,
   * as Home draws it (`components/InvitationWaits.tsx`, P118); none where it is not given.
   */
  readonly invitations?: Omit<ComponentProps<typeof InvitationWaits>, 'className'>;
}

export function Holding({ onSignedOut, reload, invitations }: HoldingProps) {
  const leaving = useSignOut(onSignedOut);
  const { stopped, again } = useReadAgain(reload, {
    on: true,
    capMs: HOLDING_CAP_MS,
    boundMs: HOLDING_BOUND_MS,
  });
  const status = useRef<HTMLDivElement>(null);

  return (
    <div className="flex min-h-dvh flex-col bg-surface-page">
      <Header signedIn />

      <main className={`${COLUMN} flex-1 px-5 py-8`}>
        {invitations === undefined ? null : <InvitationWaits {...invitations} className="mb-8" />}
        <p className="font-mono text-small tracking-widest text-text-emphasis uppercase">
          Signed in
        </p>
        <h1 className="mt-2 font-serif text-h2 font-semibold text-pretty text-text-body">
          Your home is being set up
        </h1>
        <div
          data-progress
          className="mt-5 flex items-start gap-4 rounded-card border border-border-subtle bg-surface-raised p-5"
        >
          {stopped ? null : (
            <span
              data-working
              aria-hidden="true"
              className="mt-1 block size-6 shrink-0 rounded-full border-[3px] border-surface-brand-soft border-t-surface-brand motion-safe:animate-turn"
            />
          )}
          <div ref={status} role="status" tabIndex={-1}>
            <p className="text-body text-pretty text-text-body">{HOLDING.bringingOver}</p>
            <p className="mt-2 text-body text-pretty text-text-body">
              {stopped ? HOLDING.stopped : UPDATES_BY_ITSELF}
            </p>
          </div>
        </div>
        {stopped ? (
          <button
            type="button"
            onClick={() => {
              again();
              status.current?.focus();
            }}
            className={`mt-4 ${GHOST_BUTTON}`}
          >
            {HOLDING.checkAgain}
          </button>
        ) : null}
        <p className="mt-6 text-body text-pretty">
          This page will show your home, your visits and your invoices. Until then, call us for
          anything you need.
        </p>

        <a href={PHONE_HREF} className={`mt-7 ${NAVY_BUTTON}`}>
          {`Call ${PHONE_SHOWN}`}
        </a>
        <p className="mt-4 text-small text-text-muted">{HOURS}</p>

        <div className="mt-8 border-t border-border-subtle pt-5">
          <button type="button" onClick={leaving.signOut} className={`text-body ${TEXT_LINK}`}>
            Sign out
          </button>
          <SignOutFailed failed={leaving.failed} />
        </div>
      </main>
    </div>
  );
}
